import { ApiError, type HqApi, type OrgConfigItem } from '@hq/api-sdk';
import { useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Card, Empty, Input, Select, Space, Table, Typography } from 'antd';
import { useEffect, useState } from 'react';

interface Row {
  key: string;
  value: string;
}

export interface OrgConfigPageProps {
  hq: HqApi;
  /** 平台视角（web-system）：先选租户再选机构，可代管任意租户；运营端固定本租户。 */
  platform?: boolean;
}

/**
 * 机构个性化参数（`sys_org_config`，门禁 sys:config:manage）：
 * 全局（org_id=0）打底，机构值覆盖；支持增改删，写接口幂等头由 SDK 注入。
 */
export function OrgConfigPage({ hq, platform = false }: OrgConfigPageProps) {
  const { message } = App.useApp();
  const [tenantId, setTenantId] = useState<string | undefined>();
  const [orgId, setOrgId] = useState<string | undefined>();
  const [rows, setRows] = useState<Row[]>([]);
  const [saving, setSaving] = useState(false);

  const tenants = useQuery({
    queryKey: ['sys', 'tenant', 'options'],
    queryFn: () => hq.tenant.list({}),
    enabled: platform,
    staleTime: 60 * 1000
  });

  const orgs = useQuery({
    queryKey: ['sys', 'orgs', platform ? tenantId : undefined],
    queryFn: () => hq.org.list(platform ? { tenantId } : undefined)
  });

  const detail = useQuery({
    queryKey: ['sys', 'org-config', orgId],
    queryFn: () => hq.admin.orgConfig.get(orgId!),
    enabled: Boolean(orgId)
  });

  useEffect(() => {
    if (!detail.data) return;
    setRows(
      Object.entries(detail.data.values ?? {}).map(([key, value]) => ({ key, value: value ?? '' }))
    );
  }, [detail.data]);

  const update = (index: number, patch: Partial<Row>) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const onSave = async () => {
    if (!orgId) return;
    const items: OrgConfigItem[] = rows
      .filter((row) => row.key.trim().length > 0)
      .map((row) => ({ key: row.key.trim(), value: row.value.trim() === '' ? null : row.value.trim() }));

    setSaving(true);
    try {
      const result = await hq.admin.orgConfig.save(orgId, items);
      message.success(`已保存 ${result.saved} 项配置`);
      void detail.refetch();
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async (key: string) => {
    if (!orgId || !key) return;
    try {
      await hq.admin.orgConfig.remove(orgId, key);
      message.success(`已删除 ${key}`);
      void detail.refetch();
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    }
  };

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        机构参数
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        机构级覆盖值（标题/主色/业务开关等）。未配置的键回退全局默认（org_id=0）。
      </Typography.Paragraph>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          {platform ? (
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: 220 }}
              placeholder="全部租户"
              loading={tenants.isLoading}
              value={tenantId}
              onChange={(value) => {
                setTenantId(value ?? undefined);
                setOrgId(undefined);
                setRows([]);
              }}
              options={(tenants.data ?? []).map((item) => ({
                value: String(item.id),
                label: `${item.tenantName}（${item.tenantCode}）`
              }))}
            />
          ) : null}
          <Select
            showSearch
            optionFilterProp="label"
            style={{ width: 320 }}
            placeholder="选择机构"
            loading={orgs.isLoading}
            value={orgId}
            onChange={setOrgId}
            options={(orgs.data ?? []).map((org) => ({
              value: String(org.id),
              label: `${org.orgName}（${org.orgCode}）`
            }))}
          />
          <Button type="primary" loading={saving} disabled={!orgId} onClick={onSave}>
            保存
          </Button>
          <Button onClick={() => setRows((prev) => [...prev, { key: '', value: '' }])} disabled={!orgId}>
            新增一项
          </Button>
          <Button onClick={() => void detail.refetch()} disabled={!orgId} loading={detail.isFetching}>
            刷新
          </Button>
        </Space>
      </Card>

      {detail.error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
        />
      ) : null}

      <Table<Row>
        size="small"
        rowKey={(row) => row.key || `new-${rows.indexOf(row)}`}
        loading={Boolean(orgId) && detail.isLoading}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: orgId ? <Empty description="该机构暂无个性化参数" /> : '请先选择机构' }}
        columns={[
          {
            title: '参数键',
            dataIndex: 'key',
            width: 280,
            render: (value: string, _row, index) => (
              <Input
                value={value}
                placeholder="如 ui.primaryColor"
                onChange={(e) => update(index, { key: e.target.value })}
              />
            )
          },
          {
            title: '参数值',
            dataIndex: 'value',
            render: (value: string, _row, index) => (
              <Input
                value={value}
                placeholder="留空表示恢复默认"
                onChange={(e) => update(index, { value: e.target.value })}
              />
            )
          },
          {
            title: '操作',
            width: 120,
            render: (_v, row, index) => (
              <Button
                size="small"
                danger
                onClick={() => {
                  if (row.key) void onRemove(row.key);
                  setRows((prev) => prev.filter((_item, i) => i !== index));
                }}
              >
                删除
              </Button>
            )
          }
        ]}
      />
    </div>
  );
}
